import { ButtonGradientPrimary, ButtonSecondary, Colors, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { KEY_RESULT_UNIT_LABEL_KEYS } from '../../objectiveLabels';

import type { ObjectiveDraft, ObjectiveDraftKeyResult } from '@/types/objectives';

import { KeyResultUnit } from '@/constants/KeyResultUnit';

interface ObjectiveDraftPreviewProps {
    draft: ObjectiveDraft;
    /** The form already has key results of its own, so the suggested ones will not be applied. */
    keyResultsKept: boolean;
    onRegenerate: () => void;
    onApply: () => void;
    dataTestId: string;
}

/** The AI draft as the manager reviews it before it touches the form: title, description, key results. */
export default function ObjectiveDraftPreview({
    draft,
    keyResultsKept,
    onRegenerate,
    onApply,
    dataTestId,
}: ObjectiveDraftPreviewProps) {
    const { t } = useTranslation();

    // "0 → 1" reads as nothing for a yes/no; every other unit shows its numbers and its type,
    // the same words the measurement rows use.
    const describeRange = (keyResult: ObjectiveDraftKeyResult): string => {
        if (keyResult.unit === KeyResultUnit.YES_NO) {
            return t('objectives.form.ai.keyResultRange', {
                start: t('objectives.form.no'),
                target: t('objectives.form.yes'),
            });
        }

        const unitLabel = t(KEY_RESULT_UNIT_LABEL_KEYS[keyResult.unit]);

        return `${t('objectives.form.ai.keyResultRange', {
            start: keyResult.startValue,
            target: keyResult.targetValue,
        })} (${unitLabel})`;
    };

    return (
        <div
            className={'animate-fade-in-up flex flex-col gap-5'}
            data-testid={`${dataTestId}-preview`}
        >
            <div className={'flex flex-col gap-1'}>
                <h3 className={'text-navy font-semibold max-md:text-[18px] md:text-[20px]'}>
                    {t('objectives.form.ai.previewTitle')}
                </h3>
                <p className={'text-obj-muted text-[14px]'}>
                    {t('objectives.form.ai.previewHint')}
                </p>
            </div>

            <dl
                className={
                    'border-obj-line flex flex-col gap-4 rounded-[16px] border bg-white/80 p-5'
                }
            >
                <div className={'flex flex-col gap-1'}>
                    <dt className={'text-obj-muted text-[12px] font-medium uppercase'}>
                        {t('objectives.form.ai.resultTitle')}
                    </dt>
                    <dd
                        className={'text-navy text-[16px] font-medium'}
                        data-testid={`${dataTestId}-preview-title`}
                    >
                        {draft.title}
                    </dd>
                </div>

                <div className={'flex flex-col gap-1'}>
                    <dt className={'text-obj-muted text-[12px] font-medium uppercase'}>
                        {t('objectives.form.ai.resultDescription')}
                    </dt>
                    <dd
                        className={'text-navy text-[14px] whitespace-pre-line'}
                        data-testid={`${dataTestId}-preview-description`}
                    >
                        {draft.description}
                    </dd>
                </div>

                {draft.keyResults.length > 0 && (
                    <div className={'flex flex-col gap-1'}>
                        <dt className={'text-obj-muted text-[12px] font-medium uppercase'}>
                            {t('objectives.form.ai.resultKeyResults')}
                        </dt>
                        <dd>
                            <ul
                                className={'flex flex-col gap-1'}
                                data-testid={`${dataTestId}-preview-key-results`}
                            >
                                {draft.keyResults.map((keyResult) => (
                                    <li
                                        key={keyResult.title}
                                        className={'text-navy flex items-start gap-2 text-[14px]'}
                                    >
                                        {/* The icon and the range keep their size and sit on the
                                            first line, at the left and right edges of every row;
                                            only the title in between shrinks and wraps. */}
                                        <Icon
                                            name={'essential-tick-circle'}
                                            size={'sm'}
                                            colorClassName={'text-obj-accent'}
                                            className={'mt-0.5 shrink-0'}
                                        />
                                        <span className={'min-w-0 flex-1'}>{keyResult.title}</span>
                                        <span
                                            className={'text-obj-muted shrink-0 whitespace-nowrap'}
                                        >
                                            {describeRange(keyResult)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {keyResultsKept && (
                                <p
                                    className={'text-obj-muted mt-2 text-[12px]'}
                                    data-testid={`${dataTestId}-preview-key-results-kept`}
                                >
                                    {t('objectives.form.ai.keyResultsKept')}
                                </p>
                            )}
                        </dd>
                    </div>
                )}
            </dl>

            <div className={'mt-4 flex items-center justify-end gap-3'}>
                <ButtonSecondary
                    type={'button'}
                    size={'md'}
                    onClick={onRegenerate}
                    leftIcon={
                        <Icon
                            name={'arrow-refresh-circle'}
                            size={'sm'}
                            color={'inherit'}
                        />
                    }
                    dataTestId={`${dataTestId}-regenerate`}
                >
                    {t('objectives.form.ai.regenerate')}
                </ButtonSecondary>

                <ButtonGradientPrimary
                    type={'button'}
                    size={'md'}
                    onClick={onApply}
                    leftIcon={
                        <Icon
                            name={'essential-tick-circle'}
                            size={'md'}
                            color={Colors.WHITE}
                        />
                    }
                    dataTestId={`${dataTestId}-apply`}
                >
                    {t('objectives.form.ai.apply')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
}
